// Arbiter-chosen order of the sidebar tabs (#arbiter-nav).
//
// An arbiter drags a tab to a new place; the order is kept on the profile as
// `userProfile.navOrder` (a list of data-target values), so it travels with the
// account through the routine user-doc sync rather than staying on one device.
//
// Every tab is in the list, hidden ones too — which tabs show depends on the
// role and on whether a tournament is open, and a tab that was hidden while the
// arbiter dragged must not lose its place. A tab the saved list does not know
// (added to the app after the order was saved) keeps its default neighbour.
//
// Players and guests keep the default order: the tabs they see are few, and the
// ask was the arbiter's working layout.
//
// An entry is a direct child of the nav: a tab (keyed by its data-target) or a
// group of tabs such as Other Tournaments (keyed by its data-nav-key), which
// moves as one and keeps its own tabs in their written order.

import { state } from '../state.js';

const nav = document.getElementById('arbiter-nav');

const ENTRY_KIND = '.nav-item[data-target], [data-nav-key]';
const ENTRY = ':scope > .nav-item[data-target], :scope > [data-nav-key]';

function entryKey(el) {
    return el.getAttribute('data-nav-key') || el.getAttribute('data-target');
}

function navItems() {
    return Array.from(nav.querySelectorAll(ENTRY));
}

// The nav entry (direct child of the nav) that `node` is inside, if any.
function entryOf(node) {
    while (node && node.parentElement !== nav) node = node.parentElement;
    return node && node.matches(ENTRY_KIND) ? node : null;
}

function currentOrder() {
    return navItems().map(entryKey);
}

// Captured before anything is moved: the order as written in index.html.
const DEFAULT_ORDER = nav ? currentOrder() : [];

// The saved order, completed with any tab it does not mention.
export function resolveNavOrder(saved, defaults = DEFAULT_ORDER) {
    const order = (Array.isArray(saved) ? saved : []).filter((t, i, arr) =>
        defaults.includes(t) && arr.indexOf(t) === i);
    defaults.forEach((target, i) => {
        if (order.includes(target)) return;
        const before = defaults[i - 1];
        const at = before ? order.indexOf(before) + 1 : 0;
        order.splice(at, 0, target);
    });
    return order;
}

function isArbiter() {
    const loggedIn = window.authApp && window.authApp.isAuthenticated();
    const role = state.userProfile && state.userProfile.role;
    return !!loggedIn && (!role || role === 'organizer' || role === 'organizer_player');
}

function customOrder() {
    return isArbiter() ? state.userProfile.navOrder : null;
}

// Put the tabs in the profile's order (or back to the default). Cheap when
// nothing changes, so it can run on every UI refresh.
export function applyNavOrder() {
    if (!nav) return;
    const want = resolveNavOrder(customOrder());
    const arbiter = isArbiter();
    const items = navItems();
    items.forEach(el => {
        el.draggable = arbiter;
        el.classList.toggle('nav-draggable', arbiter);
    });
    // A tab inside a group is a link, and links drag natively: switch that off
    // so grabbing it drags the whole group instead.
    nav.querySelectorAll('[data-nav-key] a').forEach(a => { a.draggable = false; });
    if (currentOrder().join() === want.join()) return;
    const byTarget = new Map(items.map(el => [entryKey(el), el]));
    want.forEach(target => nav.appendChild(byTarget.get(target)));
}

export function hasCustomNavOrder() {
    const saved = customOrder();
    return Array.isArray(saved) && resolveNavOrder(saved).join() !== DEFAULT_ORDER.join();
}

function saveNavOrder(order) {
    const navOrder = order.join() === DEFAULT_ORDER.join() ? null : order;
    if (window.app && typeof window.app.setUserProfile === 'function') {
        window.app.setUserProfile({ navOrder });
    }
}

export function resetNavOrder() {
    saveNavOrder(DEFAULT_ORDER);
}

export function initNavOrder() {
    if (!nav) return;

    let dragged = null;
    let orderBefore = '';

    nav.addEventListener('dragstart', (e) => {
        const item = entryOf(e.target);
        if (!item || !item.draggable) return;
        dragged = item;
        orderBefore = currentOrder().join();
        e.dataTransfer.effectAllowed = 'move';
        // Firefox will not start a drag without data.
        e.dataTransfer.setData('text/plain', entryKey(item));
        requestAnimationFrame(() => item.classList.add('nav-dragging'));
    });

    // The tab moves as the pointer does, so where it will land is plain to see.
    nav.addEventListener('dragover', (e) => {
        if (!dragged) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const over = entryOf(e.target);
        if (!over || over === dragged) return;
        const rect = over.getBoundingClientRect();
        const after = e.clientY > rect.top + rect.height / 2;
        nav.insertBefore(dragged, after ? over.nextSibling : over);
    });

    nav.addEventListener('drop', (e) => {
        if (dragged) e.preventDefault();
    });

    nav.addEventListener('dragend', (e) => {
        if (!dragged) return;
        dragged.classList.remove('nav-dragging');
        dragged = null;
        if (e.dataTransfer.dropEffect === 'none') {
            // Dropped outside the sidebar or cancelled with Escape: put it back.
            applyNavOrder();
            return;
        }
        const order = currentOrder();
        if (order.join() !== orderBefore) saveNavOrder(order);
    });

    applyNavOrder();
}
